import { useEffect, useSyncExternalStore } from 'react';
import type {
  ListMapObjectsResponse,
  ListMapArrowsResponse,
  MapObjectDTO,
  MapArrowDTO,
  OperationPlanDTO,
  WarPlanDTO,
  MapSettingsDTO,
} from '@warplan/shared';
import { queryClient } from './query-client.js';
import { MAP_OBJECTS_QUERY_KEY } from './map-objects-api.js';
import { MAP_ARROWS_QUERY_KEY } from './map-arrows-api.js';
import { WAR_PLAN_QUERY_KEY } from './war-plan-api.js';
import { OPERATION_PLAN_QUERY_KEY } from './operation-plan-api.js';
import { MAP_SETTINGS_QUERY_KEY } from './map-settings-api.js';

/**
 * Subscribe to the realtime channel. Updates:
 *   - `['presence']`        ← presence.changed
 *   - `['map-objects']`     ← object.created / updated / deleted
 */
export function useRealtime(): void {
  useEffect(() => {
    const source = new EventSource('/api/events', { withCredentials: true });

    const onPresence = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: { count?: number } };
        const count = parsed.payload?.count;
        if (typeof count === 'number') {
          queryClient.setQueryData(['presence'], count);
        }
        // Presence membership changed → refresh the connected-users list
        // if anyone is currently subscribed (e.g. the presence modal).
        void queryClient.invalidateQueries({ queryKey: ['presence', 'users'] });
      } catch {
        /* malformed presence event — ignore */
      }
    };

    const onObjectCreated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: MapObjectDTO };
        const obj = parsed.payload;
        if (!obj) return;
        queryClient.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) => {
          if (!prev) return { objects: [obj] };
          if (prev.objects.some((o) => o.id === obj.id)) return prev;
          return { objects: [...prev.objects, obj] };
        });
      } catch {
        /* ignore */
      }
    };

    const onObjectUpdated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: MapObjectDTO };
        const obj = parsed.payload;
        if (!obj) return;
        queryClient.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) => {
          if (!prev) return { objects: [obj] };
          return { objects: prev.objects.map((o) => (o.id === obj.id ? obj : o)) };
        });
      } catch {
        /* ignore */
      }
    };

    const onObjectDeleted = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: { id?: string } };
        const id = parsed.payload?.id;
        if (!id) return;
        queryClient.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) =>
          prev ? { objects: prev.objects.filter((o) => o.id !== id) } : prev,
        );
      } catch {
        /* ignore */
      }
    };

    const onObjectsCleared = (): void => {
      queryClient.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, { objects: [] });
    };

    const onArrowCreated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: MapArrowDTO };
        const arrow = parsed.payload;
        if (!arrow) return;
        queryClient.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) => {
          if (!prev) return { arrows: [arrow] };
          if (prev.arrows.some((a) => a.id === arrow.id)) return prev;
          return { arrows: [...prev.arrows, arrow] };
        });
      } catch {
        /* ignore */
      }
    };

    const onArrowUpdated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: MapArrowDTO };
        const arrow = parsed.payload;
        if (!arrow) return;
        queryClient.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) => {
          if (!prev) return { arrows: [arrow] };
          return { arrows: prev.arrows.map((a) => (a.id === arrow.id ? arrow : a)) };
        });
      } catch {
        /* ignore */
      }
    };

    const onArrowDeleted = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: { id?: string } };
        const id = parsed.payload?.id;
        if (!id) return;
        queryClient.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) =>
          prev ? { arrows: prev.arrows.filter((a) => a.id !== id) } : prev,
        );
      } catch {
        /* ignore */
      }
    };

    const onArrowsCleared = (): void => {
      queryClient.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, { arrows: [] });
    };

    const onWarPlanUpdated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: WarPlanDTO };
        const dto = parsed.payload;
        if (!dto) return;
        queryClient.setQueryData<WarPlanDTO>(WAR_PLAN_QUERY_KEY, dto);
      } catch {
        /* ignore */
      }
    };

    const onOperationPlanUpdated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: OperationPlanDTO };
        const dto = parsed.payload;
        if (!dto) return;
        queryClient.setQueryData<OperationPlanDTO>(OPERATION_PLAN_QUERY_KEY, dto);
      } catch {
        /* ignore */
      }
    };

    const onMapSettingsUpdated = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { payload?: MapSettingsDTO };
        const dto = parsed.payload;
        if (!dto) return;
        queryClient.setQueryData<MapSettingsDTO>(MAP_SETTINGS_QUERY_KEY, dto);
      } catch {
        /* ignore */
      }
    };

    source.addEventListener('presence.changed', onPresence as EventListener);
    source.addEventListener('object.created', onObjectCreated as EventListener);
    source.addEventListener('object.updated', onObjectUpdated as EventListener);
    source.addEventListener('object.deleted', onObjectDeleted as EventListener);
    source.addEventListener('objects.cleared', onObjectsCleared as EventListener);
    source.addEventListener('arrow.created', onArrowCreated as EventListener);
    source.addEventListener('arrow.updated', onArrowUpdated as EventListener);
    source.addEventListener('arrow.deleted', onArrowDeleted as EventListener);
    source.addEventListener('arrows.cleared', onArrowsCleared as EventListener);
    source.addEventListener('warplan.updated', onWarPlanUpdated as EventListener);
    source.addEventListener('operation-plan.updated', onOperationPlanUpdated as EventListener);
    source.addEventListener('mapsettings.updated', onMapSettingsUpdated as EventListener);
    return () => {
      source.removeEventListener('presence.changed', onPresence as EventListener);
      source.removeEventListener('object.created', onObjectCreated as EventListener);
      source.removeEventListener('object.updated', onObjectUpdated as EventListener);
      source.removeEventListener('object.deleted', onObjectDeleted as EventListener);
      source.removeEventListener('objects.cleared', onObjectsCleared as EventListener);
      source.removeEventListener('arrow.created', onArrowCreated as EventListener);
      source.removeEventListener('arrow.updated', onArrowUpdated as EventListener);
      source.removeEventListener('arrow.deleted', onArrowDeleted as EventListener);
      source.removeEventListener('arrows.cleared', onArrowsCleared as EventListener);
      source.removeEventListener('warplan.updated', onWarPlanUpdated as EventListener);
      source.removeEventListener('operation-plan.updated', onOperationPlanUpdated as EventListener);
      source.removeEventListener('mapsettings.updated', onMapSettingsUpdated as EventListener);
      source.close();
    };
  }, []);
}

export function usePresence(): number {
  const subscribe = (callback: () => void): (() => void) =>
    queryClient.getQueryCache().subscribe((event) => {
      const key: unknown = event.query.queryKey;
      if (Array.isArray(key) && key.length === 1 && key[0] === 'presence') callback();
    });
  const getSnapshot = (): number => queryClient.getQueryData<number>(['presence']) ?? 0;
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
